import { useNavigate } from 'react-router-dom';
import { CalendarDays, CalendarRange } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/Skeleton';
import { ProcurementCalendarView } from '@/components/procurement/calendar/ProcurementCalendarView';
import { usePlanningModel } from '@/features/manufacturing/planning/hooks';
import { useAuth } from '@/app/AuthProvider';

/**
 * Procurement calendar — the buying side of production planning on one
 * big month grid: which vendor to order from on which day, what goes on
 * the PO, the last safe day for each, when stock runs out if we don't,
 * and when placed POs land. Every vendor order has a one-click
 * "Create draft PO".
 */
export function ProcurementCalendarPage() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const { model, isLoading, error } = usePlanningModel();
  const canCreate = role === 'super_admin' || role === 'admin';

  if (model && model.schedules.length > 0) {
    return <ProcurementCalendarView model={model} canCreate={canCreate} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Procurement"
        title="Procurement calendar"
        actions={
          <Button variant="outline" onClick={() => navigate('/mfg/planning')}>
            <CalendarRange className="h-4 w-4" />
            Production planning
          </Button>
        }
      />

      {error ? (
        <p className="rounded-md border border-fresh-salmon/60 bg-fresh-salmon/15 px-4 py-3 text-sm text-fresh-salmon-dark">
          Couldn’t load the plan: {error.message}
        </p>
      ) : isLoading || !model ? (
        <Card>
          <CardContent className="p-0">
            <div className="grid grid-cols-7 gap-px bg-border">
              {Array.from({ length: 35 }).map((_, i) => (
                <div key={i} className="min-h-28 bg-card p-2">
                  <Skeleton className="mb-2 h-4 w-6" />
                  <Skeleton className="h-5 w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <EmptyState
              icon={CalendarDays}
              title="Nothing to schedule yet"
              detail="The procurement calendar is driven by production targets. Add what you plan to build in Production planning and the order dates, deadlines and draft POs appear here."
              action={
                <Button onClick={() => navigate('/mfg/planning')}>
                  <CalendarRange className="h-4 w-4" />
                  Set production targets
                </Button>
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
